import React, { useEffect, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from "react-bootstrap/Container";
import Image from "react-bootstrap/Image";
import NavDropdown from "react-bootstrap/NavDropdown";
import { Route } from "../utils/Routes";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useUserContext } from "../context/UserContext";
import { accentColor } from "../styles/Colors";
import { userService } from "../services/UserService";

const headerLinks = [
  { link: Route.Community, text: "Community" },
  { link: Route.CreateHub, text: "Create" },
  { link: Route.WorkoutBuilder, text: "Workout Builder" },
];

export const MainNavbar = () => {
  const { isLoggedIn, currentUser, logout } = useUserContext();
  const displayName = currentUser?.name ?? currentUser?.username ?? "Account";
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadUserProfile = async () => {
      if (!isLoggedIn || !currentUser?._id) {
        if (mounted) {
          setResolvedAvatarUrl(null);
        }
        return;
      }

      const user = await userService.getUser(currentUser._id);
      if (!mounted) {
        return;
      }
      setResolvedAvatarUrl(user?.profilePic || user?.avatarUrl || null);
    };

    loadUserProfile();

    return () => {
      mounted = false;
    };
  }, [isLoggedIn, currentUser?._id]);

  const initials = displayName.charAt(0).toUpperCase();

  return (
    <Navbar
      expand="lg"
      bg="dark"
      variant="dark"
      sticky="top"
      className="px-3"
      style={{ backgroundColor: "#12130F" }}
    >
      <Container fluid className="px-0">
        <Navbar.Brand href="/" className="d-flex align-items-center gap-2">
          <Image src="/favicon-32x32.png" alt="Logo" width={32} height={32} />
          <span className="fw-bold" style={{ color: "#bced09" }}>
            Strong Foundation
          </span>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="main-navbar" />
        <Navbar.Collapse id="main-navbar">
          <Nav className="ms-auto gap-3 align-items-center">
            {headerLinks.filter((link) => {
              if(isLoggedIn) {
                return true;
              }
              return link.link !== Route.CreateHub; // Hide "Create" link when not logged in
            }).map((link, index) => (
              <Nav.Link
                key={index}
                href={link.link}
                className="nav-link-custom"
              >
                {link.text}
              </Nav.Link>
            ))}

            {isLoggedIn ? (
              <NavDropdown
                title={
                  <span className="d-inline-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: "#3d3d3d",
                        border: `1px solid ${accentColor}`,
                        color: accentColor,
                        fontSize: 13,
                        marginRight: 4,
                        overflow: "hidden",
                      }}
                    >
                      {resolvedAvatarUrl ? (
                        <Image
                          src={resolvedAvatarUrl}
                          alt={displayName}
                          width={32}
                          height={32}
                          style={{ objectFit: "cover" }}
                        />
                      ) : initials}
                    </span>
                    <span style={{ color: "#f5f5f0", fontSize: 14 }}>{displayName}</span>
                  </span>
                }
                id="user-nav-dropdown"
                align="end"
                menuVariant="dark"
              >
                {currentUser?._id ? (
                  <NavDropdown.Item href={Route.Profile.replace(":id", currentUser._id)}>
                    My Profile
                  </NavDropdown.Item>
                ) : null}
                <NavDropdown.Item
                  onClick={logout}
                  style={{ color: "#ff4d4f" }}
                >
                  Sign Out
                </NavDropdown.Item>
              </NavDropdown>
            ) : (
              <Nav.Link
                href={Route.Login}
                className="fw-semibold px-3 py-1 rounded-pill"
                style={{
                  backgroundColor: accentColor,
                  color: "#12130f",
                  fontSize: 14,
                }}
              >
                Sign In
              </Nav.Link>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

const footerLinks = [
  { link: "https://codinghouse.dev", text: "Contact" },
  {
    link: "https://www.strongfoundation.dev/privacypolicy",
    text: "Privacy policy",
  },
  {
    link: "https://projects.codinghouse.dev/project/yorel56-strong-foundation/kanban",
    text: "Backlog",
  },
];

export const Footer = () => {
  return (
    <footer
      className="footer text-white py-4 mt-auto"
      style={{ backgroundColor: "#1C1C1C" }}
    >
      <Container fluid className="px-0">
        <Row className="text-center">
          {footerLinks.map((link, index) => (
            <Col key={index} xs={12} sm={4} className="mb-2 mb-sm-0">
              <a
                href={link.link}
                className="text-decoration-none footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.text}
              </a>
            </Col>
          ))}
        </Row>
      </Container>
    </footer>
  );
};
